import { DeliveryNote } from '../deliveryNote/DeliveryNote.types';
import { Receipt } from '../invoice/Invoice.types';
import { Language } from '../Language/Language.types';
import { SmtpConfiguration } from '../office/Office.types';

/**
 * MailConfiguration that includes the neccessary data to send the mail in the smtp config and the receiver
 */
export interface MailConfiguration {
  smtpConfig: SmtpConfiguration;
  receiver: string;
  subject: string;
  receiverCC: string[];
  content: string;
  type: MailType;
  language?: Language;
  attachements?: string[];
  currMonth?: boolean;
}

/**
 * Report of the scheduled mailings inside the mailService
 */
export interface MailServiceReport {
  id: string;
  createDate: Date;
  reports: MailReport[];
}

/**
 * Report of a single mailing
 */
export interface MailReport {
  id: string;
  type: MailType;
  failedInfos: MailInformation[];
  successInfos: MailInformation[];
}

/**
 * Information about a single mail
 */
export interface MailInformation {
  id: string;
  receiver: string;
  subject: string;
}

/**
 * holds the user that sends the mail and the protocol to display
 */
export interface ProtocolMailConfig extends MailConfiguration {
  activeUserId: string;
  protocolId: string;
}

/**
 * MailConfiguration together with the invoice to send
 */
export interface InvoiceMailConfiguration extends MailConfiguration {
  invoiceId: string;
  userId: string;
}

/**
 * MailConfiguration together with the invoiceIds to create and send and the active userId
 */
export interface DunningMailConfiguration extends MailConfiguration {
  invoiceId: string;
  activeUserId: string;
}
/**
 * MailConfiguration together with the project to send
 */
export interface OrderConfirmationMailConfiguration extends MailConfiguration {
  projectId: string;
  userId: string;
}

/**
 * Request class including the mailConfig as well as the configuration needed
 * for sending offers
 */
export interface OfferMailConfiguration extends MailConfiguration {
  offerId: string;
  activeUser: string;
  isOfferTop?: boolean;
  isOfferBottom?: boolean;
}

/**
 * Configuration to create and send credit notes and cancelReceipts
 */
export interface ReceiptConfig extends MailConfiguration {
  newReceipt: Receipt;
  userId: string;
}

/**
 * Request class including the mailConfig as well as the configuration needed
 * for sending a deliveryNote
 */
export interface DeliveryNoteMailConfiguration extends MailConfiguration {
  deliveryNote: DeliveryNote;
  activeUser: string;
}

/**
 * templates with content in multiple languages for a mailType
 */
export interface MailTemplate {
  id: string;
  createDate: Date;
  createdBy: string;
  lastUpdated?: Date;
  updatedBy?: string;
  type: MailType;
  mailContent: Map<Language, string>;
  subject: Map<Language, string>;
}

/**
 * Different Mails that could be sent
 */
export enum MailType {
  CONNECTION_TEST = 'CONNECTION_TEST',
  OFFER = 'OFFER',
  INVOICE = 'INVOICE',
  DELIVERY_NOTE = 'DELIVERY_NOTE',
  ORDER_CONFIRMATION = 'ORDER_CONFIRMATION',
  PROPATION_REMINDER = 'PROPATION_REMINDER',
  FEEDBACK_REMINDER = 'FEEDBACK_REMINDER',
  INTERNAL_CRM = 'INTERNAL_CRM',
  DUNNING = 'DUNNING',
  CREDIT_NOTE = 'CREDIT_NOTE',
  CANCEL_RECEIPT = 'CANCEL_RECEIPT',
  PROTOCOL = 'PROTOCOL',
  CAR_INSPECTION = 'CAR_INSPECTION',
  CAR_LEASING_END = 'CAR_LEASING_END',
}
/**
 * values used to replace variables in the mail and dunning texts
 */
export enum MailVariables {
  OFFER_NUMBER = 'OFFER_NUMBER',
  PROJECT_NUMBER = 'PROJECT_NUMBER',
  DELIVERY_NOTE_NUMBER = 'DELIVERY_NOTE_NUMBER',
  ORDER_NUMBER = 'ORDER_NUMBER',
  PROCESS_COST = 'PROCESS_COST',
  DUNNING_LEVEL = 'DUNNING_LEVEL',
  CREATE_DATE = 'CREATE_DATE',
  RECEIPT_DATE = 'RECEIPT_DATE',
  DUE_ON = 'DUE_ON',
  CUSTOMER_NAME = 'CUSTOMER_NAME',
  GREETING = 'GREETING',
  CUSTOMER_CONTACT_NAME = 'CUSTOMER_CONTACT_NAME',
  USER_FIRSTNAME = 'USER_FIRSTNAME',
  USER_LASTNAME = 'USER_LASTNAME',
  SIGNATURE = 'SIGNATURE',
  RECEIPT_NUMBER = 'RECEIPT_NUMBER',
  CREDIT_NOTE_NUMBER = 'CREDIT_NOTE_NUMBER',
  CANCEL_RECEIPT_NUMBER = 'CANCEL_RECEIPT_NUMBER',
  COMPANY = 'COMPANY',
  DUNNING_DUE_ON = 'DUNNING_DUE_ON',
  CAR_INSPECTION_DATE = 'CAR_INSPECTION_DATE',
  CAR_ASSIGNED_USER = 'CAR_ASSIGNED_USER',
  CAR_MODEL = 'CAR_MODEL',
  CAR_BRAND = 'CAR_BRAND',
  REGISTRATION_PLATE = 'REGISTRATION_PLATE',
  LEASING_END_DATE = 'LEASING_END_DATE',
}
